import chunk from 'lodash/chunk';
import sumBy from 'lodash/sumBy';
import toInteger from 'lodash/toInteger';

import JIL_CONSTANTS from 'api/jil/JilConstants';
import jilOrganizationsProductsLicenseGroups from 'api/jil/jilOrganizationsProductsLicenseGroups';
import log from 'services/log';
import JilModelList from 'services/modelList/JilModelList';

import LicenseGroup from './LicenseGroup';
import {LICENSE_GROUP_LIST_CACHE_ID} from './LicenseGroupConstants';

class LicenseGroupList extends JilModelList {
  /**
   * @description Method to construct a new LicenseGroupList.
   *
   * @param {Object} options - LicenseGroupList instance settings
   * @param {String} options.orgId - organization id
   * @param {Product} options.product - the model of the product whose list of license groups should be returned
   *
   */
  constructor(options) {
    super({
      // Beware that this model's refresh is attaching the product to every license group item in the list.
      // There is also a method returning the product without cloning it.
      // If this list is to be cached these need to be considered when determining the cache key
      // and what events should clear the cache.
      isCacheable: false,
      itemClassRef: LicenseGroup,
      modelCacheId: LICENSE_GROUP_LIST_CACHE_ID,
      resource: jilOrganizationsProductsLicenseGroups.getLicenseGroups,
      sortExpression: JIL_CONSTANTS.SORT.NAME,
      sortOrder: JIL_CONSTANTS.ORDER.ASC,
      ...options,
    });

    Object.assign(this, {
      orgId: options.orgId,
      product: options.product,
    });
  }

  getProduct() {
    return this.product;
  }

  /**
   * @description Function to return the total quota allocated to the list of
   *    license groups by summing the totalQuantity.
   *
   * @returns {Number} sum of the distributed quotas
   */
  getTotalAllocatedQuota() {
    return sumBy(this.items, (licenseGroup) => toInteger(licenseGroup.totalQuantity));
  }

  /**
   * @description Method to load the license group list
   * @returns {Promise<LicenseGroupList>} promise resolved with refreshed license groups,
   *                    otherwise rejects with an error
   */
  async refresh() {
    try {
      await super.refresh({
        orgId: this.orgId,
        productId: this.product.id,
      });

      this.items.forEach((licenseGroup) => {
        Object.assign(licenseGroup, {
          orgId: this.orgId,
          product: this.product,
        });
      });
    } catch (error) {
      log.error('LicenseGroupList.refresh() failed. Error:', error);
      return Promise.reject(error);
    }
    return this;
  }

  /**
   * @description Method to save changes to LicenseGroupList to the back-end.
   * @param {Object} [options] - custom options for saving
   * @param {Boolean} [options.refresh] - whether to refresh after saving, defaults to true for legacy list.
   *
   * @returns {Promise<LicenseGroupList>} resolves to updated state if changes successfully saved, else rejects with error message
   */
  async save({refresh = true} = {}) {
    try {
      if (this.addedItems.length > 0 || this.removedItems.length > 0) {
        await saveAddedItems(this);
        this.resetAddedItems();

        await removeItems(this);
        this.resetRemovedItems();

        if (refresh) {
          this.filter.lastQuery = this.filter.query;
          this.filter.query = '';
          return this.refresh();
        }

        return this;
      }
      return this;
    } catch (error) {
      log.error('LicenseGroupList.save() failed. Error:', error);
      return Promise.reject(error);
    }
  }

  /**
   * @description Method to modify the license quota for product configs. Like src1,
   * it will split PATCH calls into 5 license group ops per request until API performance is improved.
   *
   * @param {Array<LicenseGroup>} licenseGroups - array of license groups to modify license quota
   * @returns {Promise} promise object resolved when update is complete, or rejected with an error
   */
  async updateQuota(licenseGroups) {
    const operations = licenseGroups.map((licenseGroup) => ({
      op: 'replace',
      path: `/${licenseGroup.id}/TOTALQUANTITY/${licenseGroup.totalQuantity}`,
    }));

    try {
      // Until API performance is improved, split up the PATCH operations into 5 license group per request.
      // This is emulating the previous behavior from src1, except we do not wait on each request before sending the next.
      const CHUNK_SIZE = 5;
      const operationChunks = chunk(operations, CHUNK_SIZE);

      const promises = operationChunks.map((operationChunk) =>
        jilOrganizationsProductsLicenseGroups.patchLicenseGroups(
          {
            orgId: this.orgId,
            productId: this.product.id,
          },
          operationChunk
        )
      );
      await Promise.all(promises);

      await this.refresh();

      return this;
    } catch (error) {
      log.error('LicenseGroupList failed to update quotas. Error: ', error);
      return Promise.reject(error);
    }
  }
}

/** Private methods */

async function saveAddedItems(licenseGroupList) {
  await Promise.all(licenseGroupList.addedItems.map((item) => item.save()));
}

async function removeItems(licenseGroupList) {
  const operations = convertRemovedItemsToPatchOperation(licenseGroupList.removedItems);

  const response = await jilOrganizationsProductsLicenseGroups.patchLicenseGroups(
    {
      orgId: licenseGroupList.orgId,
      productId: licenseGroupList.product.id,
    },
    operations
  );
  return response;

  function convertRemovedItemsToPatchOperation(items) {
    return items.map((licenseGroup) => ({
      op: 'remove',
      path: `/${licenseGroup.id}`,
    }));
  }
}

export default LicenseGroupList;
